'use strict';

import extend from 'lodash/extend';

/**
 * Low-level communication handler for the Content service.
 * Uses Q-based promises for all operations.
 */
(function ($) {
  window.atex = window.atex || {};
  window.atex.aceui = window.atex.aceui || {};
  window.atex.aceui.service = window.atex.aceui.service || {};

  /**
   * Enum for update modes. Determines how a content update is applied.
   *
   * @readonly
   * @enum {string}
   */
  atex.aceui.service.UpdateMode = {
    /**
     * Each aspect in the submitted update request overwrites the latest version of that aspect.
     * Aspects in the latest version that are not submitted in the update request will be left untouched.
     */
    Update: 'update',

    /**
     * Each aspect in the submitted update request is deep merged with the latest version of that aspect.
     * Aspects in the latest version that are not submitted in the update request will be left untouched.
     */
    Merge: 'merge',

    /**
     * Completely replaces the latest version of the content.
     */
    Replace: 'replace'
  };

  /**
   * Creates a new ContentService instance.
   *
   * @param {string} contentServiceUrl - The URL of the backend service
   * @param {Object} [serviceSettings] - Settings for the ContentService
   * @param {Object} [serviceSettings.customQ = window.Q] - An implementation of a promise based Q library, i.e. Angular $q.
   * @param {boolean} [serviceSettings.cacheBusting = false] - Indicates whether cache busting strategies should be
   * used when reading content from the server in order to get around browser caching bugs (read: Safari).
   *
   * @typedef {Object} ContentService
   * @property {function} getContent
   * @property {function} getContentVersion
   * @property {function} getContentHistory
   * @property {function} getContentMetadata
   * @property {function} createContent
   * @property {function} updateContent
   * @property {function} deleteContent
   *
   * @return {ContentService}
   */
  atex.aceui.service.ContentService = function(contentServiceUrl, serviceSettings) {
    var settings = extend({ cacheBusting: false }, serviceSettings);
    var q = settings.customQ || window.Q;

    return {
      /**
       * Reads a content.
       *
       * In the normal case the returned content is the latest version of the content. If the content was requested
       * on a specific view, the version being assigned to that view will be returned.
       *
       * The content can be retrieved in a specific variant. If a representation of the content could not be
       * produced in the requested variant, the operation will fail with an atex.aceui.error.Error.
       *
       * Contents which are public, i.e. assigned to view 'acePublic', may be retrieved without an authentication token.
       * In all other cases an authentication token must be supplied.
       *
       * Typical retrieval of a content:
       *
       * @example
       * ContentService.getContent({
       *   alias: 'myNamespace/article1',
       *   authToken: ... // Retrieved from AuthenticationService
       * })
       * .then(function(result) {
       *   ... = result.content.aspects.MyAspect.field;
       * })
       *
       *
       * In the example below the content is fetched from a specific view in a specific variant:
       *
       * @example
       * ContentService.getContent({
       *   alias: 'myNamespace/article1',
       *   view: 'acePublic',
       *   variant: 'teaser'
       * })
       * .then(function(result) {
       *   ... = result.content.aspects.MyTeaser.headline;
       * })
       *
       * @param {Object} requestArgs - Request arguments
       * @param {Alias} requestArgs.alias - An alias identifying the content to be retrieved
       * @param {number|string} [requestArgs.atLeastRevision] - Get the content with revision number atLeastRevision or later
       * @param {string} [requestArgs.variant] - A variant name
       * @param {string} [requestArgs.view] - Name of a view from which the content should be retrieved.
       * @param {boolean} [requestArgs.fullTypeInfo=false] - Annotates the returned content with full type information
       * @param {AuthToken} [requestArgs.authToken] - Authentication token as retrieved from the AuthenticationService
       *
       * @typedef {Object} ContentResult - The result of a successfully retrieved content
       * @property {Content} content - The content as retrieved from the server.
       * @property {string} revision - Opaque string representing a certain revision of the content.
       *
       * @returns {Promise.<ContentResult, atex.aceui.error.Error>}
       */
      getContent: function(requestArgs) {
        var deferred = q.defer();

        var urlEndpointAndId = '/alias/' + requestArgs.alias;
        var variantString = requestArgs.variant ? '?variant=' + requestArgs.variant + '&' : '?';
        var viewString = requestArgs.view ? '/view/' + requestArgs.view : '';
        var allTypes = requestArgs.fullTypeInfo === true ? '+allTypes' : '';

        var fullUrl = contentServiceUrl + viewString + urlEndpointAndId + variantString + 'format=json' + allTypes;

        if (settings.cacheBusting === true) {
          fullUrl = fullUrl + '&cacheBusting=' + Math.random().toString().substring(2);
        }

        if (requestArgs.atLeastRevision) {
          fullUrl += `&atLeastRevision=${requestArgs.atLeastRevision}`;
        }

        $.ajax(fullUrl, {
          dataType: 'json',

          beforeSend: function(jqXHR) {
            if (requestArgs.authToken !== undefined) {
              jqXHR.setRequestHeader("X-Auth-Token", requestArgs.authToken);
            }
          },

          success: function(data, textStatus, jqXHR) {
            var eTag = jqXHR.getResponseHeader('ETag');
            deferred.resolve({ content: data, revision: eTag });
          },

          error: function(jqXHR, textStatus, errorThrown) {
            var e = atex.aceui.HTTPUtils.createError(jqXHR, 'atex.aceui.service.ContentService', {
              401: 'Could not authenticate user while getting content "' + requestArgs.alias + '" from the server',
              403: 'Permission denied when getting "' + requestArgs.alias + '" from the server',
              404: 'Content "' + requestArgs.alias + '" was not found on the server (404)',
              'default': 'There was an error while getting content "' + requestArgs.alias + '" from the server'
            });

            if (typeof e.rootCause !== 'undefined') {
              e.rootCause.textStatus = textStatus;
              e.rootCause.errorThrown = errorThrown;
              e.rootCause.requestArgs = requestArgs;
            }

            deferred.reject(e);
          }
        });

        return deferred.promise;
      },

      /**
       * Reads a content version.
       *
       * Note: Only use this operation to read a specific version of a content. To read the latest version or the version
       * which is currently assigned to a view, instead use {@link ContentService#getContent}.
       *
       * Content version can be retrieved from the system field content.system.version in the content retrieved, you can
       * also retrieve a list of history by using {@link ContentService#getContentHistory}.
       *
       * The content version can be retrieved in a specific variant. If a representation of the content could not be
       * produced in the requested variant, the operation will fail with an atex.aceui.error.Error.
       *
       * Typical retrieval of a content version:
       *
       * @example
       * ContentService.getContentVersion({
       *   version: ...,  // Retrieved from elsewhere
       *   authToken: ... // Retrieved from AuthenticationService
       * })
       * .then(function(result) {
       *   ... = result.content.aspects.MyAspect.field;
       * })
       *
       * @param {Object} requestArgs - Request arguments
       * @param {Version} requestArgs.version - Identifying the content version to be fetched
       * @param {number|string} [requestArgs.atLeastRevision] - Get the content with revision number atLeastRevision or later
       * @param {AuthToken} requestArgs.authToken - Authentication token
       * @param {string} [requestArgs.variant] - A variant name
       * @param {boolean} [requestArgs.fullTypeInfo = false] - Annotates the returned content with full type information
       *
       * @typedef {Object} ContentResult - The result of a successfully retrieved content
       * @property {Content} content - The content as retrieved from the server.

       * @returns {Promise.<ContentResult, atex.aceui.error.Error>}
       */
      getContentVersion: function(requestArgs) {
        var deferred = q.defer();

        var urlEndpointAndId = '/version/' + requestArgs.version;
        var variantString = requestArgs.variant ? '?variant=' + requestArgs.variant + '&' : '?';
        var allTypes = requestArgs.fullTypeInfo === true ? '+allTypes' : '';

        var fullUrl = contentServiceUrl + urlEndpointAndId + variantString + 'format=json' + allTypes;

        if (settings.cacheBusting === true) {
          fullUrl = fullUrl + '&cacheBusting=' + Math.random().toString().substring(2);
        }

        if (requestArgs.atLeastRevision) {
          fullUrl += `&atLeastRevision=${requestArgs.atLeastRevision}`;
        }

        $.ajax(fullUrl, {
          dataType: 'json',

          beforeSend: function(jqXHR) {
            if (requestArgs.authToken !== undefined) {
              jqXHR.setRequestHeader("X-Auth-Token", requestArgs.authToken);
            }
          },

          success: function(data, textStatus, jqXHR) {
            deferred.resolve({ content: data });
          },

          error: function(jqXHR, textStatus, errorThrown) {
            var e = atex.aceui.HTTPUtils.createError(jqXHR, 'atex.aceui.service.ContentService', {
              401: 'Could not authenticate user while getting content version "' + requestArgs.version + '" from the server',
              403: 'Permission denied when getting content version "' + requestArgs.version + '" from the server',
              404: 'Content version "' + requestArgs.version + '" was not found on the server (404)',
              'default': 'There was an error while getting content version "' + requestArgs.version + '" from the server'
            });

            if (typeof e.rootCause !== 'undefined') {
              e.rootCause.textStatus = textStatus;
              e.rootCause.errorThrown = errorThrown;
              e.rootCause.requestArgs = requestArgs;
            }

            deferred.reject(e);
          }
        });

        return deferred.promise;
      },

      /**
       * Creates a new content. Include all mandatory aspects.
       *
       * On success the created content is also returned. Note that the returned content may be different from the content
       * being passed in case any modifying lifecycle hooks were triggered on the server.
       *
       * @example
       * ContentService.createContent({
       *   content: {
       *     "system": {
       *       "contentType": "doc_example_content"
       *     },
       *     "aspects": {
       *       "doc_example": {
       *         "title": "Hello, world!"
       *       }
       *     }
       *   },
       *
       *   authToken: ...
       * })
       * .then(function(result) {
       *   var id = result.content.system.id;
       *   ...
       * })
       *
       * @param {Object} requestArgs - Request arguments
       * @param {Content} requestArgs.content - An object representing the content to be created.
       * @param {AuthToken} requestArgs.authToken - An authentication token
       * @param {boolean} [requestArgs.fullTypeInfo = false] - Annotates the returned content with type information
       *
       * @typedef {Object} ContentResult - The result of a successfully created content
       * @property {Content} content - The content as stored on the server.
       * @property {string} revision - Opaque string representing the created revision of the content.
       *
       * @returns {Promise.<ContentResult, atex.aceui.error.Error>}
       */
      createContent: function(requestArgs) {
        var deferred = q.defer();

        var content = JSON.parse(JSON.stringify(requestArgs.content));
        delete content.status;

        var fullUrl = contentServiceUrl + '?format=json';

        if (requestArgs.fullTypeInfo) {
          fullUrl = fullUrl + '+allTypes';
        }

        $.ajax(fullUrl, {
          type: 'POST',
          dataType: 'json',
          contentType: 'application/json',
          data: JSON.stringify(content),

          beforeSend: function(jqXHR) {
            if (requestArgs.authToken !== undefined) {
              jqXHR.setRequestHeader("X-Auth-Token", requestArgs.authToken);
            }
          },

          success: function(data, textStatus, jqXHR) {
            var eTag = jqXHR.getResponseHeader('ETag');
            deferred.resolve({ content: data, revision: eTag });
          },

          error: function(jqXHR, textStatus, errorThrown) {
            var e = atex.aceui.HTTPUtils.createError(jqXHR, 'atex.aceui.service.ContentService', {
              401: 'Could not authenticate user while creating new content on the server',
              403: 'Permission denied when creating new content on the server',
              'default': 'There was an error while creating new content on the server'
            });

            if (typeof e.rootCause !== 'undefined') {
              e.rootCause.textStatus = textStatus;
              e.rootCause.errorThrown = errorThrown;
              e.rootCause.requestArgs = requestArgs;
            }

            deferred.reject(e);
          }
        });

        return deferred.promise;
      },

      /**
       * Updates an existing content, you can send the same content back using updateContent (although the id, version
       * and meta fields will be ignored). Any aspects which you don't want to update may be omitted.
       *
       * You can also add an list of operations. If only includeing operations, a new version won't be created.
       *
       * Also returns the updated content. Note that the returned content may be different from the content
       * being passed in case any lifecycle hooks were triggered on the server.
       *
       * @example
       * ContentService.updateContent({
       *   alias: 'myNamespace/article1',
       *   origin: revision,
       *   updateMode: 'replace',
       *   fullTypeInfo: true,
       *
       *   content: {
       *     "system": {
       *       "contentType": "doc_example_content"
       *     },
       *     "aspects": {
       *       "doc_example": {
       *         "_type": "doc_example",
       *         "title": "Hello update!"
       *       }
       *     },
       *     "operations": [
       *       {
       *         "_type": "aceAssignToViews",
       *         "views": ["acePublic"]
       *       }
       *     ],
       *   },
       *
       *   authToken: ...
       * })
       * .then(function(result) {
       *   var id = result.content.system.id;
       *   ...
       * })
       *
       * @param {Object} requestArgs - Request arguments
       * @param {Alias} requestArgs.alias - An alias identifying the content to be updated.
       * @param {string} requestArgs.revision - The revision that this update is based on. Enables conflict detection.
       * @param {Content} requestArgs.content - An object containing the data for the content to be updated.
       * @param {AuthToken} requestArgs.authToken - An authentication token.
       * @param {UpdateMode} [requestArgs.updateMode = Update] -
       *        Defines if and how submitted aspects shall be combined with existing aspects.
       * @param {boolean} [requestArgs.fullTypeInfo = false] - Annotates the returned content with type information.
       *
       * @typedef {Object} ContentResult - The result of a successfully updated content.
       * @property {Content} content - The content as retrieved from the server.
       * @property {string} revision - Opaque string representing a certain revision of the content.
       *
       * @returns {Promise.<ContentResult, atex.aceui.error.Error>}
       */
      updateContent: function(requestArgs) {
        if (!requestArgs.alias) {
          var e = atex.aceui.error.Error({
            type: atex.aceui.error.types.INVALID_PARAMETERS,
            cause: "No alias property present in requestArgs object",
            origin: "atex.aceui.service.ContentService"
          });

          return q.reject(e);
        }

        var deferred = q.defer();

        var content = JSON.parse(JSON.stringify(requestArgs.content));
        delete content.status;

        var formatParam = '?format=json' + (requestArgs.fullTypeInfo ? '+allTypes' : '');
        var updateModeParam = requestArgs.updateMode ? '&updateMode=' + requestArgs.updateMode : '';
        var url = contentServiceUrl + '/alias/' + requestArgs.alias + formatParam + updateModeParam;

        $.ajax(url, {
          type: 'PUT',
          dataType: 'json',
          contentType: 'application/json',
          data: JSON.stringify(content),

          beforeSend: function(jqXHR) {
            if (requestArgs.authToken) {
              jqXHR.setRequestHeader("X-Auth-Token", requestArgs.authToken);
            }

            if (requestArgs.revision) {
              jqXHR.setRequestHeader("If-Match", requestArgs.revision);
            }
          },

          success: function(data, textStatus, jqXHR) {
            var eTag = jqXHR.getResponseHeader('ETag');
            deferred.resolve({ content: data, revision: eTag });
          },

          error: function(jqXHR, textStatus, errorThrown) {
            var e = atex.aceui.HTTPUtils.createError(jqXHR, 'atex.aceui.service.ContentService', {
              401: 'Could not authenticate user when storing content "' + requestArgs.alias + '" to the server',
              403: 'Permission denied when storing content  "' + requestArgs.alias + '" to the server',
              409: 'Conflict with server version while storing content "' + requestArgs.alias + '" to the the server',
              'default': 'There was an error while storing content  "' + requestArgs.alias + '" to the server'
            });

            if (typeof e.rootCause !== 'undefined') {
              e.rootCause.textStatus = textStatus;
              e.rootCause.errorThrown = errorThrown;
              e.rootCause.requestArgs = requestArgs;
            }

            deferred.reject(e);
          }
        });

        return deferred.promise;
      },

      /**
       * Deletes a content using one of it's aliases.
       *
       * @param {Object} requestArgs - Request arguments
       * @param {Alias} requestArgs.alias - An alias identifying the content to be deleted.
       * @param {string} requestArgs.revision  - Opaque string representing a certain revision of the content.
       * @param {AuthToken} requestArgs.authToken - An authentication token.
       *
       * @returns {Promise.<void, atex.aceui.error.Error>}
       */
      deleteContent: function(requestArgs) {
        if (!requestArgs.alias) {
          var e = atex.aceui.error.Error({
            type: atex.aceui.error.types.INVALID_PARAMETERS,
            cause: "No alias property present in requestArgs object",
            origin: "atex.aceui.service.ContentService"
          });

          return q.reject(e);
        }

        var deferred = q.defer();

        var urlEndpointAndId = 'alias/' + requestArgs.alias;
        var fullUrl = contentServiceUrl + '/' + urlEndpointAndId;

        $.ajax(fullUrl, {
          type: 'DELETE',
          dataType: 'json',

          beforeSend: function(jqXHR) {
            if (requestArgs.authToken) {
              jqXHR.setRequestHeader("X-Auth-Token", requestArgs.authToken);
            }

            if (requestArgs.revision) {
              jqXHR.setRequestHeader("If-Match", requestArgs.revision);
            }
          },

          success: function() {
            deferred.resolve();
          },

          error: function(jqXHR, textStatus, errorThrown) {
            var e = atex.aceui.HTTPUtils.createError(jqXHR, 'atex.aceui.service.ContentService', {
              401: 'Could not authenticate user when deleting content "' + requestArgs.alias + '" from the server',
              403: 'Permission denied when deleting content "' + requestArgs.alias + '" from the server',
              409: 'Conflict with server version while deleting content "' + requestArgs.alias + '" from the server',
              'default': 'There was an error while deleting content "' + requestArgs.alias + '"from the server'
            });

            if (typeof e.rootCause !== 'undefined') {
              e.rootCause.textStatus = textStatus;
              e.rootCause.errorThrown = errorThrown;
              e.rootCause.requestArgs = requestArgs;
            }

            deferred.reject(e);
          }
        });

        return deferred.promise;
      },

      /**
       * Retrieves history information for a content.
       *
       * @example
       * "versions": [
       *   {
       *     "version": "onecms:b3b65750-f1fd-49ce-b323-61028605a623:3878d5a0-b4e1-4a5b-82d7-721fca7a5f91",
       *     "revision": {
       *       "contentId": "onecms:b3b65750-f1fd-49ce-b323-61028605a623",
       *       "revisionNumber": 0
       *     },
       *       "creationTime": 1511357609987,
       *       "creatorId": "admin",
       *       "views": []
       *   }
       * ]
       *
       * @param {Object} requestArgs - Request arguments
       * @param {Alias} requestArgs.alias - An alias identifying the content for which to retrieve history info
       * @param {AuthToken} [requestArgs.authToken] - An authentication token
       * @param {number|string} [requestArgs.atLeastRevision] - Get the history for the content with revision number atLeastRevision or later
       *
       * @typedef {Object} HistoryResult - The result of a successfully retrieved content history.
       * @property {Object} versions - The content history as retrieved from the server.
       * @property {string} revision - Opaque string representing the current revision of the content.
       *
       * @returns {Promise.<HistoryResult, atex.aceui.error.Error>}
       */
      getContentHistory: function(requestArgs) {
        var deferred = q.defer();

        var urlEndpointAndId = 'alias/' + requestArgs.alias;
        var fullUrl = contentServiceUrl + '/' + urlEndpointAndId + '/history';

        if (requestArgs.atLeastRevision) {
          fullUrl += `?atLeastRevision=${requestArgs.atLeastRevision}`;
        }

        $.ajax(fullUrl, {
          dataType: 'json',

          beforeSend: function(jqXHR) {
            if (requestArgs.authToken !== undefined) {
              jqXHR.setRequestHeader("X-Auth-Token", requestArgs.authToken);
            }
          },

          success: function(data, textStatus, jqXHR) {
            var eTag = jqXHR.getResponseHeader('ETag');
            deferred.resolve({ history: data, revision: eTag });
          },

          error: function(jqXHR, textStatus, errorThrown) {
            var e = atex.aceui.HTTPUtils.createError(jqXHR, 'atex.aceui.service.ContentService', {
              401: 'Could not authenticate user when retrieving content history for "' + requestArgs.alias + '" from the server',
              403: 'Permission denied when retrieving content history for "' + requestArgs.alias + '" from the server',
              'default': 'There was an error while retrieving content history for "' + requestArgs.alias + '"from the server'
            });

            if (typeof e.rootCause !== 'undefined') {
              e.rootCause.textStatus = textStatus;
              e.rootCause.errorThrown = errorThrown;
              e.rootCause.requestArgs = requestArgs;
            }

            deferred.reject(e);
          }
        });

        return deferred.promise;
      },

      /**
       * Get a content´s metadata.
       *
       *
       * @example
       * {
       *   "system": {},
       *   "aspects": {
       *     "_type": "_contentInfo",
       *     "aceAliases": {
       *       "_type": "aceAliases",
       *       "aliases": {
       *         "_type": "MAP::LIST::string",
       *         "contentid": [
       *           "9034880f-920a-4d0c-a3cc-91ae6664ecc7"
       *         ]
       *       }
       *     }
       *   },
       *   "status": {}
       * }
       *
       * @param {Object} requestArgs - Request arguments
       * @param {Alias} requestArgs.alias - An alias identifying the content for which to retrieve metadata
       * @param {AuthToken} requestArgs.authToken - An authentication token
       * @param {number|string} [requestArgs.atLeastRevision] - Get metadata for the content with revision number atLeastRevision or later
       *
       * @typedef {Object} MetadataResult - The result of a successfully retrieved content metadata
       * @property {Object} aspects - A list of metadata aspects
       * @property {string} revision - Opaque string representing the current revision of the content.
       *
       * @returns {Promise.<MetadataResult, atex.aceui.error.Error>}
       */
      getContentMetadata: function (requestArgs) {
        var deferred = q.defer();

        var urlEndpointAndId = 'alias/' + requestArgs.alias;
        var fullUrl = contentServiceUrl + '/' + urlEndpointAndId + '/metadata?format=json+allTypes'; // TODO allTypes?

        if (requestArgs.atLeastRevision) {
          fullUrl += `&atLeastRevision=${requestArgs.atLeastRevision}`;
        }

        $.ajax(fullUrl, {
          dataType: 'json',

          beforeSend: function(jqXHR) {
            if (requestArgs.authToken !== undefined) {
              jqXHR.setRequestHeader("X-Auth-Token", requestArgs.authToken);
            }
          },

          success: function(data, textStatus, jqXHR) {
            var eTag = jqXHR.getResponseHeader('ETag');
            deferred.resolve({ metadata: data, revision: eTag });
          },

          error: function(jqXHR, textStatus, errorThrown) {
            var e = atex.aceui.HTTPUtils.createError(jqXHR, 'atex.aceui.service.ContentService', {
              401: 'Could not authenticate user while getting content info for "' + requestArgs.alias + '" from the server',
              403: 'Permission denied when getting "' + requestArgs.alias + '" from the server',
              404: 'ContentMetadata for "' + requestArgs.alias + '" was not found on the server (404)',
              'default': 'There was an error while getting content info "' + requestArgs.alias + '" from the server'
            });

            if (typeof e.rootCause !== 'undefined') {
              e.rootCause.textStatus = textStatus;
              e.rootCause.errorThrown = errorThrown;
              e.rootCause.requestArgs = requestArgs;
            }

            deferred.reject(e);
          }
        });

        return deferred.promise;
      }
    };
  };
})(jQuery);
